import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Eye, EyeOff, Mail, Lock, User, Key } from 'lucide-react';
import PasswordReset from './PasswordReset';

type AuthMode = 'login' | 'register';

const Login: React.FC = () => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  
  const { login, register, isLoading, error, clearError, user } = useAuth();
  const navigate = useNavigate();

  // Single redirect effect - only redirect if user is logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const validatePasswords = () => {
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setPasswordError(null);

    if (mode === 'register' && !validatePasswords()) {
      return;
    }

    try {
      switch (mode) {
        case 'login':
          await login(email, password);
          // Login successful, useEffect will handle redirect
          break;
        case 'register':
          await register(name, email, password, confirmPassword);
          // Registration successful, useEffect will handle redirect
          break;
      }
    } catch (error) {
      // Error is handled by AuthContext
    }
  };



  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setShowPassword(false);
    setPasswordError(null);
    clearError();
  };

  const getModeTitle = () => {
    switch (mode) {
      case 'login': return 'Welcome to MoneyCare';
      case 'register': return 'Create Account';
      default: return 'Welcome to MoneyCare';
    }
  };

  const getModeDescription = () => {
    switch (mode) {
      case 'login': return 'Enter your credentials to access your account';
      case 'register': return 'Create a new account to get started';
      default: return 'Enter your credentials to access your account';
    }
  };

  const renderForm = () => {
    switch (mode) {
      case 'login':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button
                type="button"
                variant="link"
                className="text-sm"
                onClick={() => setShowPasswordReset(true)}
              >
                Forgot password?
              </Button>
              <Button
                type="button"
                variant="link"
                className="text-sm"
                onClick={() => {
                  resetForm();
                  setMode('register');
                  setPasswordError(null);
                }}
              >
                Create account
              </Button>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Sign In'}
            </Button>
          </>
        );

      case 'register':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
                        <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (confirmPassword && e.target.value !== confirmPassword) {
                      setPasswordError('Passwords do not match');
                    } else if (confirmPassword && e.target.value === confirmPassword) {
                      setPasswordError(null);
                    }
                  }}
                  className="pl-10 pr-10"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (password && e.target.value !== password) {
                      setPasswordError('Passwords do not match');
                    } else if (password && e.target.value === password) {
                      setPasswordError(null);
                    }
                  }}
                  className="pl-10 pr-10"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {passwordError && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded-md">
                  {passwordError}
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="link"
              className="text-sm"
              onClick={() => {
                resetForm();
                setMode('login');
                setPasswordError(null);
              }}
            >
              Already have an account? Sign in
            </Button>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Create Account'}
            </Button>
          </>
        );


    }
  };

  // Show PasswordReset component if requested
  if (showPasswordReset) {
    return (
      <PasswordReset onBackToLogin={() => setShowPasswordReset(false)} />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex flex-col items-center space-y-3">
            <img 
              src="/moneycare-logo.png" 
              alt="MoneyCare Logo" 
              className="h-16 w-16 object-contain"
            />
            <div>
              <CardTitle className="text-2xl font-bold text-center">
                {getModeTitle()}
              </CardTitle>
              <CardDescription className="text-center">
                {getModeDescription()}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {renderForm()}

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            {/* The submit button is now rendered inside renderForm for both modes */}
          </form>


        </CardContent>
      </Card>
    </div>
  );
};

export default Login; 